import React, { useState, useEffect } from "react";
import styled from "styled-components";
import google from "../images/google.png";
import cloud from "../images/cloud.png";
import BitcoinMoney from "../images/BitcoinMoney.jpeg";
import ShehacksRedefined from "../images/ShehacksRedefined.png";
import SelflessLeadership from "../images/SelflessLeadership.jpeg";
import "../styles/styles.css";
import "../styles/blog.css";

const StyledSimpleA = styled.a`
  color: #e68976;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    color: #d97963;
    text-decoration: none;
  }
`;

const StyledA = styled.a`
  background-color: #e68976;
  border: none;
  color: white;
  font-style: normal;
  padding: 12px 32px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #d97963;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(230, 137, 118, 0.3);
    text-decoration: none;
    color: white;
  }
`;

const StyledEffect = styled.a`
  color: #000;
  text-decoration: underline;
  text-decoration-color: #e68976;
  text-decoration-thickness: 2px;
  text-underline-offset: 4px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1rem;
  
  &:hover {
    color: #e68976;
    text-decoration-color: #d97963;
  }
  
  &::after {
    content: "↗";
    font-size: 1rem;
    color: #e68976;
    display: inline-block;
    transition: transform 0.3s ease;
  }
  
  &:hover::after {
    transform: translate(2px, -2px);
  }
`;

// Circular image container for podcasts with improved hover effects
const CircularImageContainer = styled.div`
  width: 200px;
  height: 200px;
  margin: 0 auto 20px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    
    img {
      transform: scale(1.1);
    }
  }
`;

// Rectangular image container for blogs with consistent sizing and improved hover
const RectangularImageContainer = styled.div`
  width: 100%;
  height: 240px;
  overflow: hidden;
  border-radius: 12px;
  margin-bottom: 20px;
  background-color: #f0f0f0;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    
    img {
      transform: scale(1.05);
    }
  }
`;

const PodcastCard = styled.div`
  flex: 0 0 calc(33.333% - 30px);
  min-width: 240px;
  max-width: 300px;
  text-align: center;
  padding: 20px;
  
  @media (max-width: 992px) {
    flex: 0 0 calc(50% - 20px);
  }
  
  @media (max-width: 576px) {
    flex: 0 0 100%;
  }
`;

const BlogCard = styled.div`
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const CardTitle = styled.h5`
  text-align: center;
  color: #0A2338;
  margin-bottom: 20px;
  min-height: 48px;
  font-size: 16px;
  line-height: 1.4;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SectionLabel = styled.h3`
  color: #e68976;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const SectionTitle = styled.h2`
  color: #0A2338;
  font-size: 2.5rem;
  font-weight: 700;
  margin-top: 10px;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Blog = () => {
  const [podcasts, setPodcasts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [podcastsLoading, setPodcastsLoading] = useState(true);
  const [blogsLoading, setBlogsLoading] = useState(true);

  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        const response = await fetch('/api/podcasts');
        if (!response.ok) throw new Error('Failed to fetch podcasts');
        const data = await response.json();
        
        const recentPodcasts = data.podcasts
          .filter(podcast => podcast.published !== false)
          .sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return new Date(b.date) - new Date(a.date);
          })
          .slice(0, 3);
        
        setPodcasts(recentPodcasts || []);
        setPodcastsLoading(false);
      } catch (err) {
        console.error('Error fetching podcasts:', err);
        setPodcasts([
          {
            title: "Google's VP of User Experience",
            image: google,
            link: "https://open.spotify.com/show/5as4zJCEU6zHAv0hwdZGFE?si=InbX0VusQO6vwCWbeDd2EA"
          },
          {
            title: "In the Cloud with Erin Chapple",
            image: cloud,
            link: "https://open.spotify.com/show/5as4zJCEU6zHAv0hwdZGFE?si=InbX0VusQO6vwCWbeDd2EA"
          }
        ]);
        setPodcastsLoading(false);
      }
    };

    fetchPodcasts();
  }, []);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const response = await fetch('/api/blogs');
        if (!response.ok) throw new Error('Failed to fetch blogs');
        const data = await response.json();
        
        const recentBlogs = data.blogs
          .filter(blog => blog.published !== false)
          .sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return new Date(b.date) - new Date(a.date);
          })
          .slice(0, 3);
        
        setBlogs(recentBlogs || []);
        setBlogsLoading(false);
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setBlogs([
          {
            title: "BTC Better Have My Money: Big Tech and Bitcoin",
            image: BitcoinMoney,
            link: "https://wits-uwo.medium.com/btc-better-have-my-money-big-tech-and-bitcoin-8803603ce1d9"
          },
          {
            title: "Re-Introducing SheHacks+",
            image: ShehacksRedefined,
            link: "https://wits-uwo.medium.com/re-introducing-shehacks-13d09c10a149"
          },
          {
            title: "Selfless Leadership",
            image: SelflessLeadership,
            link: "https://wits-uwo.medium.com/selfless-leadership-c2c5fb1b1b6f"
          }
        ]);
        setBlogsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  return (
    <div style={{ height: "100%", display: "block", margin: "0", overflow: "hidden" }}>
      {/* HEADER SECTION */}
      <section
        style={{
          backgroundColor: "#F4D089",
          paddingTop: "8%",
          paddingBottom: "4%",
        }}
      >
        <div className="container">
          <div className="row pt-4">
            <div className="col-lg-6 m-auto">
              <h1>
                Blog &<br />
                Podcast
              </h1>
            </div>
            <div className="col-lg-6 py-4">
              <h4
                style={{
                  fontFamily: "nunito",
                  fontWeight: "600",
                  lineHeight: "1.6",
                  color: "#0A2338",
                }}
              >
                Hear and read the thoughts of powerful women+ in tech! Join us as
                we delve into their stories and insights.
              </h4>
            </div>
          </div>
        </div>
      </section>

      {/* PODCAST SECTION */}
      <div style={{ padding: "80px 0" }}>
        <div className="container">
          <div className="row" style={{ marginBottom: "40px" }}>
            <div className="col">
              <SectionLabel>PODCAST</SectionLabel>
              <SectionTitle>TidBits with WITS</SectionTitle>
              <div style={{ marginBottom: "16px" }}>
                <StyledEffect
                  href="https://open.spotify.com/show/5as4zJCEU6zHAv0hwdZGFE?si=InbX0VusQO6vwCWbeDd2EA"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  TidBits with WITS is now available on Spotify!
                </StyledEffect>
              </div>
              <h5
                style={{
                  color: "#0A2338",
                  fontFamily: "nunito",
                  lineHeight: "1.6",
                  marginTop: "16px",
                  maxWidth: "900px",
                  fontSize: "1.1rem"
                }}
              >
                TidBits with WITS invites industry professionals to share their own 
                experiences, insights, and advice to enter the tech career space. 
                Join us as we explore the stories of influential women+ in tech - 
                one conversation at a time.
              </h5>
            </div>
          </div>

          {podcastsLoading ? (
            <div className="row">
              <div className="col text-center py-5">
                <p style={{ color: "#0A2338" }}>Loading podcasts...</p>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: "60px" }}>
              <div style={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: "40px", 
                justifyContent: "center" 
              }}>
                {podcasts.map((podcast, index) => (
                  <PodcastCard key={podcast.id || index}>
                    <StyledSimpleA
                      href={podcast.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <CircularImageContainer>
                        <img 
                          src={podcast.image || google} 
                          alt={podcast.title}
                          onError={(e) => { e.target.src = google; }}
                        />
                      </CircularImageContainer>
                    </StyledSimpleA>
                    <CardTitle>{podcast.title}</CardTitle>
                    <StyledA
                      href={podcast.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Listen on Spotify
                    </StyledA>
                  </PodcastCard>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BLOG SECTION */}
      <div style={{ backgroundColor: "#F2D0C8", padding: "80px 0" }}>
        <div className="container">
          <div className="row" style={{ marginBottom: "50px" }}>
            <div className="col">
              <SectionLabel>BLOG</SectionLabel>
              <SectionTitle>
                Insights, inspirations, <br /> and more!
              </SectionTitle>
            </div>
          </div>
          
          {blogsLoading ? (
            <div className="row">
              <div className="col text-center py-5">
                <p style={{ color: "#0A2338" }}>Loading blogs...</p>
              </div>
            </div>
          ) : (
            <div className="row" style={{ rowGap: "30px" }}>
              {blogs.map((blog, index) => (
                <div className="col-lg-4 col-md-6 col-sm-12" key={blog.id || index}>
                  <BlogCard>
                    <StyledSimpleA 
                      href={blog.link} 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <RectangularImageContainer>
                        <img 
                          src={blog.image || BitcoinMoney} 
                          alt={blog.title}
                          onError={(e) => { e.target.src = BitcoinMoney; }}
                        />
                      </RectangularImageContainer>
                    </StyledSimpleA>
                    <CardTitle>{blog.title}</CardTitle>
                  </BlogCard>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Blog;